import { useId } from 'react';
import cx from 'classnames';
import s from './Select.module.scss';
import ReactSelect, { components as selectComponents, StylesConfig } from 'react-select';
import FieldWrapper from '../FieldWrapper';
import { IconClear, IconSortUnfold } from "@/shared/ui/Icons";

type SelectProps = {
  label?: string;
  labelInfo?: string;
  className?: string;
  selectClassName?: string;
  withCoinTheme?: boolean;
  hint?: string;
  error?: string;
  name?: string;
  id?: string;
  full?: boolean;
  menuFull?: boolean;
  isDisabled?: boolean;
  options: { [key: string]: any }[];
  optionLabelName?: string;
  optionValueName?: string;
  value?: any;
  isLoading?: boolean;
  defaultMenuIsOpen?: boolean;
  placeholder?: string;
  styles?: StylesConfig;
  components?: { [key: string]: any };
  colorSchema?: 'default' | 'basic';
  size?: 'sm' | 'md' | 'lg';
  textSize?: 'xs' | 'sm' | 'base' | 'lg';
  controlClassName?: string;
  isMulti?: boolean;
  isCenter?: boolean;
  showDropdownIndicator?: boolean;
  isSearchable?: boolean;
  ref?: any;
  onChange?: (option: any) => void;
  [x:string]: any;
}

const Select = (props: SelectProps) => {
  const {
    label,
    labelInfo,
    className,
    selectClassName,
    withCoinTheme = false,
    hint,
    error,
    name,
    id,
    full,
    menuFull,
    isDisabled,
    options,
    optionLabelName = 'label',
    optionValueName = 'value',
    value,
    isLoading,
    placeholder,
    styles,
    components,
    colorSchema = 'default',
    size = 'md',
    textSize = 'xs',
    controlClassName,
    labelTextClassName,
    showDropdownIndicator = true,
    isCenter = false,
    onChange,
    onClick,
    ref,
    ...restProps
  } = props;

  const sizes = {
    sm: 'ui-select__sm',
    md: 'ui-select__md',
    lg: 'ui-select__lg',
  };

  const fontSizes = {
    xs: 'ui-select__text-xs',
    sm: 'ui-select__text-sm',
    base: 'ui-select__text-base',
    lg: 'ui-select__text-lg',
  };

  const colorSchemes = {
    'default': 'ui-select__default',
    'basic': 'ui-select__basic',
  };

  const classes = cx(
    'ui-select',
    selectClassName,
    sizes[size],
    fontSizes[textSize],
    colorSchemes[colorSchema],
    {"theme-ui-select": withCoinTheme},
  );

  return (
    <FieldWrapper
      label={label}
      labelInfo={labelInfo}
      labelTextClassName={labelTextClassName}
      className={className}
      hint={hint}
      error={error}
      full={full}
    >
      <div className={cx(s.inputWrap, {
        [s.hasError]: !!error,
        [s.full]: full,
      })}>
        <ReactSelect
          instanceId={useId()}
          value={value}
          styles={{
            menuPortal: (base: any) => {
              return ({
                ...base,
                marginTop: colorSchema === 'basic' ? '-25px' : null,
                zIndex: 10000000000, // more that intercom and drawer component
              })
            },
            menu: (base: any) => ({
              ...base,
              width: menuFull ? '100%' : null,
            }),
            ...styles
        }}
          options={options}
          components={{
            DropdownIndicator: (props: any) => showDropdownIndicator ? <DropdownIndicator
              colorSchema={colorSchema} 
              {...props}
            /> : null,
            ValueContainer: (props: any) => <ValueContainer
              isCenter={isCenter}
              {...props}
            />,
            Control: (props: any) => <Control className={controlClassName} {...props}/>,
            MultiValueRemove,
            IndicatorSeparator: null,
            ClearIndicator: undefined,
            ...components,
          }}
          isDisabled={isDisabled}
          isLoading={isLoading}
          getOptionLabel={option => option[optionLabelName]}
          placeholder={placeholder}
          getOptionValue={option => option[optionValueName]}
          className={classes}
          classNamePrefix="ui-select"
          ref={ref}
          menuPortalTarget={document.body}
          onChange={onChange}
          isSearchable={false}
          {...restProps}
        />
      </div>
    </FieldWrapper>
  );
}

const DropdownIndicator = (props: any) => {
  const { 
    ...restProps
  } = props;
  
  return (
    <selectComponents.DropdownIndicator {...restProps}>
      <IconSortUnfold className="w-3"/>
    </selectComponents.DropdownIndicator>
  );
};

const ValueContainer = (props: any) => {
  const { 
    isCenter = true,
    ...restProps
  } = props;
  
  return (
    <selectComponents.ValueContainer
      className={cx("flex", {
        'justify-center': isCenter,
      })}
      {...restProps}
    />
  );
};

const Control = (props: any) => {
  const {
    ...restProps
  } = props;

  return (
    <selectComponents.Control
      {...restProps}
    />
  );
};


const MultiValueRemove = (props: any) => {
  return (
    <selectComponents.MultiValueRemove {...props}>
      <IconClear />
    </selectComponents.MultiValueRemove>
  );
};


export {selectComponents};

export default Select;